import React, { CSSProperties, useState } from 'react';
import styles from './CardLigacao.module.css';
import Card from '../../components/layout/Card';
import HBox from '../../components/layout/HBox';
import Icon from '@mdi/react';
import { mdiAccountClock, mdiClock, mdiDrag, mdiHeadset, mdiPhoneHangup, mdiPhoneInTalk, mdiPhonePlus } from '@mdi/js';
import Subtitulo from '../../components/tipografia/Subtitulo';
import { Draggable } from 'react-beautiful-dnd';
import VBox from '../../components/layout/VBox';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'react-toastify';

const getItemStyle = (
    isDragging: boolean,
    draggableStyle: any,
): CSSProperties => ({
    userSelect: 'none',
    padding: 0,
    margin: 0,

    background: isDragging
        ? 'var(--tc-color-success-200)'
        : 'var(--tc-color-white)',
    opacity: isDragging ? 0.5 : 1,
    minWidth: '200px',
    width: '200px',
    maxWidth: '250px',
    ...draggableStyle,
});

const estadoDesc: Record<string, { icon: string; label: string; color: string }> = {
    aguardando: {
        icon: mdiAccountClock,
        label: 'Aguardando',
        color: 'var(--tc-color-warning)',
    },
    em_andamento: {
        icon: mdiPhoneInTalk,
        label: 'Em andamento',
        color: 'var(--tc-color-success)',
    },
    conectada: {
        icon: mdiHeadset,
        label: 'Conectada',
        color: 'var(--tc-color-success)',
    },
    finalizada: {
        icon: mdiClock,
        label: 'Finalizada',
        color: 'var(--tc-color-gray-500)',
    },
};

type CardLigacaoProps = {
    item: any;
    index: number;
    dragItem?: any;
    /** True se o gestor está atualmente nesta chamada */
    isAtiva?: boolean;
    /** True se o gestor pode entrar nesta chamada (mesmo departamento) */
    canEnter?: boolean;
    aceitarLigacao?: (chamadaId: number, participantKey: string, livekitToken?: string, livekitUrl?: string) => void;
    encerrarLigacao?: (solucionado?: boolean) => void;
};

const CardLigacao = ({ item, index, dragItem, isAtiva, canEnter, aceitarLigacao, encerrarLigacao }: CardLigacaoProps) => {
    const estado = estadoDesc[item.estado] ?? estadoDesc.aguardando;
    const [loading, setLoading] = useState(false);

    // Chamadas em andamento permitem entrada do gestor
    const podeEntrar = canEnter && ['conectada', 'em_atendimento'].includes(item.estado);

    const handleEntrar = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (loading || !aceitarLigacao) return;
        setLoading(true);
        try {
            const res = await fetch(`/api/chamada/${item.id}/entrar`, { method: 'POST', credentials: 'include' });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data?.message || 'Erro ao entrar na chamada');
            }
            const data = await res.json();
            aceitarLigacao(item.id, data.participantKey, data.livekitToken, data.livekitUrl);
        } catch (err: any) {
            toast.error(err.message || 'Erro ao entrar na chamada');
        } finally {
            setLoading(false);
        }
    };

    const handleSair = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (loading || !encerrarLigacao) return;
        setLoading(true);
        try {
            await fetch(`/api/chamada/${item.id}/entrar`, { method: 'DELETE', credentials: 'include' });
            encerrarLigacao();
        } catch (err: any) {
            toast.error(err.message || 'Erro ao sair da chamada');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Draggable
            key={item.id}
            draggableId={`ligacao-${item.id}`}
            index={index}
        >
            {(provided, snapshot) => (
                <Card
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    style={{
                        gap: '2px',
                        ...getItemStyle(
                            snapshot.isDragging || dragItem?.id === item.id,
                            provided.draggableProps.style,
                        ),
                    }}
                    title={`Chamada #${item.id}\nCanal: ${item.canal_nome || 'N/A'}`}
                >
                    <HBox
                        style={{
                            alignContent: 'center',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                    >
                        <Icon path={mdiDrag} size={1} color="var(--tc-color-gray-500)" />
                        <VBox style={{ maxWidth: 'calc(100% - 30px)' }}>
                            <HBox style={{ alignItems: 'center', justifyContent: 'center' }}>
                                <Icon
                                    path={estado.icon}
                                    size={1}
                                    color={estado.color}
                                />
                                <Subtitulo
                                    style={{
                                        whiteSpace: 'nowrap',
                                        textOverflow: 'ellipsis',
                                        overflow: 'hidden',
                                    }}
                                >
                                    {item.cliente_nome || 'Cliente'}
                                </Subtitulo>
                            </HBox>

                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '2px',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '100%',
                                    fontSize: '12px',
                                }}
                            >
                                <div style={{ color: estado.color }}>{estado.label}</div>
                                <div>
                                    Início: {item.data_inicio
                                        ? 'Faz ' + formatDistanceToNow(new Date(item.data_inicio), { locale: ptBR })
                                        : 'N/A'}
                                </div>
                            </div>

                            {podeEntrar && (
                                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '4px' }}>
                                    {isAtiva ? (
                                        <button
                                            onClick={handleSair}
                                            disabled={loading}
                                            title="Sair da chamada"
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '4px',
                                                padding: '3px 8px',
                                                fontSize: '11px',
                                                cursor: loading ? 'not-allowed' : 'pointer',
                                                background: 'var(--tc-color-danger)',
                                                color: '#fff',
                                                border: 'none',
                                                borderRadius: '4px',
                                            }}
                                        >
                                            <Icon path={mdiPhoneHangup} size="14px" color="#fff" />
                                            {loading ? 'Saindo...' : 'Sair'}
                                        </button>
                                    ) : (
                                        <button
                                            onClick={handleEntrar}
                                            disabled={loading}
                                            title="Entrar na chamada"
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '4px',
                                                padding: '3px 8px',
                                                fontSize: '11px',
                                                cursor: loading ? 'not-allowed' : 'pointer',
                                                background: 'var(--tc-color-success)',
                                                color: '#fff',
                                                border: 'none',
                                                borderRadius: '4px',
                                            }}
                                        >
                                            <Icon path={mdiPhonePlus} size="14px" color="#fff" />
                                            {loading ? 'Entrando...' : 'Entrar'}
                                        </button>
                                    )}
                                </div>
                            )}
                        </VBox>
                    </HBox>
                </Card>
            )}
        </Draggable>
    );
};

export default CardLigacao;
